import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { auth } from '../config/firebase';
import { getIdToken } from 'firebase/auth';
import {
  BillingPlansResponse,
  BillingUsageResponse,
  SubscriptionResponse,
  CheckoutSessionResponse,
  CustomerPortalResponse,
  InvoiceResponse,
  CreateCheckoutRequest,
  ChangePlanRequest,
  SubscriptionPlan,
} from '../types';

// Create a dedicated API instance for billing
const billingAxios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Longer timeout for Stripe operations
});

// Request interceptor for authentication
billingAxios.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('token');

    if (token) {
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        try {
          token = await getIdToken(firebaseUser);
          localStorage.setItem('token', token);
        } catch (error) {
          console.error('Failed to refresh Firebase token:', error);
        }
      }

      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
billingAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        try {
          const newToken = await getIdToken(firebaseUser, true);
          localStorage.setItem('token', newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return billingAxios(originalRequest);
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
        }
      }

      // Only redirect to login if user was previously authenticated
      if (localStorage.getItem('token')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Billing API
export const billingApi = {
  // Get all available subscription plans
  getPlans: (): Promise<BillingPlansResponse> =>
    billingAxios.get('/api/billing/plans').then(res => res.data),

  // Get current user's subscription
  getSubscription: (): Promise<SubscriptionResponse> =>
    billingAxios.get('/api/billing/subscription').then(res => res.data),

  // Create a checkout session for subscribing
  createCheckout: (data: CreateCheckoutRequest): Promise<CheckoutSessionResponse> =>
    billingAxios.post('/api/billing/checkout', data).then(res => res.data),

  // Create a customer portal session for managing billing
  createPortal: (): Promise<CustomerPortalResponse> =>
    billingAxios.post('/api/billing/portal').then(res => res.data),

  // Change subscription plan
  changePlan: (data: ChangePlanRequest): Promise<{ success: boolean; message: string }> =>
    billingAxios.post('/api/billing/change-plan', data).then(res => res.data),

  // Cancel subscription (at end of billing period)
  cancelSubscription: (): Promise<{ success: boolean; message: string }> =>
    billingAxios.post('/api/billing/cancel').then(res => res.data),

  // Reactivate a canceled subscription
  reactivateSubscription: (): Promise<{ success: boolean; message: string }> =>
    billingAxios.post('/api/billing/reactivate').then(res => res.data),

  // Get user's invoices
  getInvoices: (): Promise<InvoiceResponse[]> =>
    billingAxios.get('/api/billing/invoices').then(res => res.data),

  // Get current billing-period usage + caps (powers Settings progress bars)
  getUsage: (): Promise<BillingUsageResponse> =>
    billingAxios.get('/api/billing/usage').then(res => res.data),

  // Buy a prepaid token pack ("Paid Extra Usage"). Returns a Checkout URL to
  // redirect to. `packId` is one of the backend PREPAID_PACKS ids.
  topup: (packId: string): Promise<CheckoutSessionResponse> =>
    billingAxios.post('/api/billing/topup', { pack_id: packId }).then(res => res.data),

  // Referral status: the user's share link + earned/pending counts.
  getReferral: (): Promise<ReferralStatusResponse> =>
    billingAxios.get('/api/billing/referral').then(res => res.data),

  // Robustly attach a referral the signup-time capture may have missed.
  claimReferral: (code: string): Promise<{ claimed: boolean }> =>
    billingAxios.post('/api/billing/referral/claim', { code }).then(res => res.data),
};

export interface ReferralInvitee {
  masked_identity: string;
  status: string; // pending | qualified | completing | completed
  paid_months: number; // 0..3
  reward_granted: boolean;
  registered_at: string; // RFC3339
  paid_since: string | null; // RFC3339, null until they upgrade to paid
}

export interface MyReferralReward {
  status: string;
  paid_months: number;
  months_required: number;
}

export interface ReferralStatusResponse {
  referral_code: string;
  referral_link: string;
  completed_count: number;
  pending_count: number;
  months_earned: number;
  annual_cap: number;
  rewards_used_window: number;
  invitees: ReferralInvitee[];
  my_reward: MyReferralReward | null;
}

// Prepaid token packs, mirrored from the backend stripe_billing::PREPAID_PACKS.
// `amount` is in the currency's smallest unit (yen, or USD cents).
export const PREPAID_PACKS: { id: string; currency: 'jpy' | 'usd'; amount: number; tokens: number }[] = [
  { id: 'jpy_1m', currency: 'jpy', amount: 1000, tokens: 1_000_000 },
  { id: 'jpy_3m', currency: 'jpy', amount: 3000, tokens: 3_000_000 },
  { id: 'jpy_10m', currency: 'jpy', amount: 10000, tokens: 10_000_000 },
  { id: 'usd_1_5m', currency: 'usd', amount: 1000, tokens: 1_500_000 },
  { id: 'usd_4_5m', currency: 'usd', amount: 3000, tokens: 4_500_000 },
  { id: 'usd_15m', currency: 'usd', amount: 10000, tokens: 15_000_000 },
];

// Format a price for display in the chosen currency. USD/EUR-style
// currencies store cents (so we divide by 100); JPY has no sub-unit and
// passes through as integer yen.
export const formatPrice = (amount: number, currency: string = 'usd'): string => {
  const code = currency.toLowerCase();
  const isYen = code === 'jpy';
  const locale = isYen ? 'ja-JP' : 'en-US';
  const value = isYen ? amount : amount / 100;
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: isYen ? 0 : 0,
    maximumFractionDigits: isYen ? 0 : 2,
  });
  return formatter.format(value);
};

// Pricing spec B.3: JPY and USD are independent prices, not FX-converted.
// Pick the amount that matches `currency` from a SubscriptionPlan row.
// Returns the price in its native unit (yen or cents) — pass straight to
// formatPrice with the same currency.
export const pickPlanPrice = (
  plan: SubscriptionPlan,
  currency: 'jpy' | 'usd',
  interval: 'month' | 'year',
): number => {
  if (currency === 'jpy') {
    return interval === 'year' ? plan.price_yearly_jpy : plan.price_monthly_jpy;
  }
  return interval === 'year' ? plan.price_yearly : plan.price_monthly;
};

// Guess the visitor's preferred display currency. JP is the priority
// market (spec A.2), so anyone in Asia/Tokyo or whose browser language
// starts with `ja-` sees JPY; everyone else USD. The Pricing page also
// offers an explicit toggle, so this is just the default.
export const detectPreferredCurrency = (): 'jpy' | 'usd' => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz === 'Asia/Tokyo') return 'jpy';
  } catch { /* JSDOM / older browsers */ }
  const lang = (typeof navigator !== 'undefined' && navigator.language) || '';
  if (lang.toLowerCase().startsWith('ja')) return 'jpy';
  return 'usd';
};

// Helper function to format card brand for display
export const formatCardBrand = (brand: string | null | undefined): string => {
  if (!brand) return 'Card';
  const brandMap: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  };
  return brandMap[brand.toLowerCase()] || brand;
};

// Helper function to check if a feature is included in a plan
export const isPlanFeatureIncluded = (planFeatures: string[], feature: string): boolean => {
  return planFeatures.includes(feature);
};

export default billingApi;
